import {
  collapseToRanges,
  expandCodeList,
  intersectCodes,
} from "helpers/codeRanges";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { RadioButton } from "primereact/radiobutton";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import {
  ChargeBasedPricingMode,
  ChargeBasedPricingScope,
} from "types/ChargeBasedPricing/ChargeBasedPricing";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

import { sanitizeDrgCodes } from "./InpatientRulesetForm";

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldHelp = styled.span`
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const RadioLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const SCOPE_OPTIONS: { label: string; value: ChargeBasedPricingScope }[] = [
  { label: "Line charges", value: "LINE_CHARGES" },
  { label: "Total charges", value: "TOTAL_CHARGES" },
];

// CLE-2546. Only meaningful for LINE_CHARGES — a total-charges rule prices the whole claim by
// definition, so there is no per-line fee schedule left for it to defer to.
const MODE_OPTIONS: { label: string; value: ChargeBasedPricingMode }[] = [
  { label: "Replace the fee schedule", value: "REPLACE_FEE_SCHEDULE" },
  {
    label: "Only when there is no fee schedule",
    value: "ONLY_IF_NO_FEE_SCHEDULE",
  },
];

// CLE-2524 applicability choices for ladder rule types.
const APPLICABILITY_OPTIONS: {
  value: RulesetApplicability;
  label: string;
  help: string;
}[] = [
  {
    value: "SCOPED",
    label: "Payer-Provider rule",
    help: "Applies only to the selected provider and payer.",
  },
  {
    value: "TYPICAL",
    label: "Typical rule",
    help: "Template only. No effect until copied into a contract.",
  },
  {
    value: "FALLBACK",
    label: "Fallback rule",
    help: "Live — applies to every provider that has no more specific rule of this type. Optionally limit it to a single payer.",
  },
];

export interface IChargeBasedPricingFormData {
  scope: ChargeBasedPricingScope;
  mode: ChargeBasedPricingMode;
  multiplier: number;
  // Optional max-amount cap, in dollars (converted to cents at the submit boundary).
  // Null = no cap; multiplier alone determines the expected.
  maxAmountInDollars: number | null;
  providerId: string | null;
  payerId: number | null;
  codes: string[];
  excludedCodes: string[];
  // CLE-2627 — codes the payer never reimburses separately, priced at $0.
  packagedCodes: string[];
  groupName: string;
  applicability: RulesetApplicability;
}

// Most charge-based-pricing contracts are whole-claim, percent-of-charges deals
// (e.g. "45% of charges, no fee schedule"), so default scope to TOTAL_CHARGES.
export const INITIAL_CHARGE_BASED_PRICING_FORM_DATA: IChargeBasedPricingFormData =
  {
    scope: "TOTAL_CHARGES",
    mode: "REPLACE_FEE_SCHEDULE",
    multiplier: 1,
    maxAmountInDollars: null,
    providerId: null,
    payerId: null,
    codes: [],
    excludedCodes: [],
    packagedCodes: [],
    groupName: "",
    applicability: "SCOPED",
  };

interface IChargeBasedPricingFormProps {
  title: string;
  submitLabel: string;
  formData: IChargeBasedPricingFormData;
  onChange: (data: IChargeBasedPricingFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /** CLE-2524: applicability is immutable after create — edit mode locks the radio group. */
  scopeLocked?: boolean;
}

export function ChargeBasedPricingForm({
  title,
  submitLabel,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  scopeLocked,
}: IChargeBasedPricingFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    const base = (payers?.data ?? []).map((p: any) => ({
      label: p.name,
      value: p.id,
    }));
    return [{ label: "All payers", value: null }, ...base];
  }, [payers]);

  // CLE-2597 — keep the scope fields consistent with the chosen applicability, so a
  // provider left over from SCOPED can't be submitted (and silently dropped) as a fallback.
  const handleApplicabilityChange = (value: RulesetApplicability) => {
    onChange({
      ...formData,
      applicability: value,
      providerId: value === "SCOPED" ? formData.providerId : null,
      payerId: value === "TYPICAL" ? null : formData.payerId,
    });
  };

  const handleChange = <K extends keyof IChargeBasedPricingFormData>(
    field: K,
    value: IChargeBasedPricingFormData[K]
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const isValid =
    !!formData.scope &&
    (formData.applicability !== "SCOPED" || formData.providerId !== null) &&
    !isSubmitting;

  // Same overlap-warning logic as lesser-of-charges — expand ranges first so codes
  // hidden inside a "0300-0420" range still show up as overlaps, then collapse the
  // intersection back to ranges for the human-readable warning text.
  const overlappingCodes = useMemo(
    () =>
      collapseToRanges(
        intersectCodes(
          expandCodeList(formData.codes),
          expandCodeList(formData.excludedCodes)
        )
      ),
    [formData.codes, formData.excludedCodes]
  );

  // CLE-2627 — "excluded" and "packaged" sound like synonyms and are not: excluding a code hands
  // its line back to the fee schedule, packaging it pays nothing. Listing a code in both is almost
  // always a mistake about which one the user meant, so say which one actually takes effect.
  const packagedAlsoExcluded = useMemo(
    () =>
      collapseToRanges(
        intersectCodes(
          expandCodeList(formData.packagedCodes),
          expandCodeList(formData.excludedCodes)
        )
      ),
    [formData.packagedCodes, formData.excludedCodes]
  );

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
          aria-label="Back"
        />
        <h2 className="m-0">{title}</h2>
      </div>

      {/* Important framing: this rule REPLACES standard pricing, it doesn't cap it.
          Worth surfacing prominently so users don't confuse it with lesser-of-charges. */}
      <Message
        severity="warn"
        className="mb-4"
        content={
          <div>
            <strong>Charge-Based Pricing replaces the standard pricing</strong>{" "}
            for matching claims. When this rule fires, the expected amount is
            computed as <code>multiplier × billed charges</code>, ignoring any
            fee schedule, inpatient outliers, or lesser-of cap. Use this for
            contracts that pay purely as a percentage of charges with no fee
            schedule.
          </div>
        }
      />

      <Card className="mb-4">
        <FieldGrid>
          <Field>
            <label htmlFor="scope">Scope</label>
            <Dropdown
              inputId="scope"
              value={formData.scope}
              options={SCOPE_OPTIONS}
              onChange={(e) => handleChange("scope", e.value)}
              placeholder="Select scope"
            />
            <FieldHelp>
              Total charges prices the claim as a whole; Line charges prices
              each CPT-bearing line, leaving lines this rule does not list on
              their fee-schedule price.
            </FieldHelp>
          </Field>
          {formData.scope === "LINE_CHARGES" && (
            <Field>
              <label htmlFor="mode">When a fee schedule already exists</label>
              <Dropdown
                inputId="mode"
                value={formData.mode}
                options={MODE_OPTIONS}
                onChange={(e) => handleChange("mode", e.value)}
                placeholder="Select behavior"
              />
              <FieldHelp>
                Replace = charges always win. Only when there is no fee schedule
                = charges fill the gaps, and any line the fee schedule already
                prices keeps that price.
              </FieldHelp>
            </Field>
          )}
          <Field>
            <label htmlFor="multiplier">Multiplier</label>
            <InputNumber
              inputId="multiplier"
              value={formData.multiplier}
              onValueChange={(e) => handleChange("multiplier", e.value ?? 1)}
              mode="decimal"
              minFractionDigits={2}
              maxFractionDigits={4}
              min={0}
              max={99}
            />
            <FieldHelp>
              0.45 = 45% of charges, 1.00 = 100% of charges.
            </FieldHelp>
          </Field>
        </FieldGrid>

        <FieldGrid>
          <Field>
            <label htmlFor="maxAmount">Max amount (optional)</label>
            <InputNumber
              inputId="maxAmount"
              value={formData.maxAmountInDollars}
              onValueChange={(e) =>
                handleChange(
                  "maxAmountInDollars",
                  e.value === undefined ? null : e.value
                )
              }
              mode="currency"
              currency="USD"
              locale="en-US"
              min={0}
              placeholder="Leave empty for no cap"
            />
            <FieldHelp>
              Ceiling on the priced expected per claim. Example: &quot;78% of
              charges, capped at $1,500&quot; → multiplier 0.78, max amount
              $1,500. Leave empty for no cap.
            </FieldHelp>
          </Field>
        </FieldGrid>

        <RadioGroup>
          {APPLICABILITY_OPTIONS.map((option) => (
            <RadioLine key={option.value}>
              <RadioButton
                inputId={`applicability-${option.value}`}
                name="applicability"
                value={option.value}
                checked={formData.applicability === option.value}
                disabled={scopeLocked}
                onChange={() => handleApplicabilityChange(option.value)}
              />
              <label
                htmlFor={`applicability-${option.value}`}
                style={{ fontWeight: 600, cursor: "pointer" }}
              >
                {option.label}
              </label>
              <FieldHelp>{option.help}</FieldHelp>
            </RadioLine>
          ))}
        </RadioGroup>

        {formData.applicability === "SCOPED" && (
          <FieldGrid>
            <Field>
              <label htmlFor="providerId">Provider</label>
              <Dropdown
                inputId="providerId"
                value={formData.providerId}
                options={providerOptions}
                onChange={(e) => handleChange("providerId", e.value ?? null)}
                placeholder="Select a provider"
                filter
                disabled={scopeLocked}
              />
            </Field>
            <Field>
              <label htmlFor="payerId">Payer</label>
              <Dropdown
                inputId="payerId"
                value={formData.payerId}
                options={payerOptions}
                onChange={(e) => handleChange("payerId", e.value ?? null)}
                placeholder="Select a payer"
                filter
                disabled={scopeLocked}
              />
              <FieldHelp>
                Leave as &quot;All payers&quot; to apply this rule to every
                payer for this provider.
              </FieldHelp>
            </Field>
          </FieldGrid>
        )}

        {/* CLE-2597 — a fallback may name one payer: the same floor, every provider. */}
        {formData.applicability === "FALLBACK" && (
          <FieldGrid>
            <Field>
              <label htmlFor="fallbackPayerId">Payer</label>
              <Dropdown
                inputId="fallbackPayerId"
                value={formData.payerId}
                options={payerOptions}
                onChange={(e) => handleChange("payerId", e.value ?? null)}
                placeholder="All payers"
                filter
                disabled={scopeLocked}
              />
              <FieldHelp>
                Leave as &quot;All payers&quot; for the global fallback. Pick a
                payer to floor that payer across every provider — a rule written
                for a specific provider still wins over this.
              </FieldHelp>
            </Field>
          </FieldGrid>
        )}

        <FieldGrid>
          <Field>
            <label htmlFor="codes">Codes</label>
            <Chips
              inputId="codes"
              value={formData.codes}
              onChange={(e) =>
                handleChange("codes", sanitizeDrgCodes(e.value ?? []))
              }
              placeholder="96523, J1642, 470, 0272-0372"
              separator=","
            />
            <FieldHelp>
              Enter individual codes (96523, J1642) or ranges (0272-0372). The
              rule applies to any claim that contains at least one of these
              codes. Leave empty to price every claim from this provider/payer
              at <code>multiplier × charges</code> (the most common case).
            </FieldHelp>
          </Field>
          <Field>
            <label htmlFor="excludedCodes">Excluded Codes</label>
            <Chips
              inputId="excludedCodes"
              value={formData.excludedCodes}
              onChange={(e) =>
                handleChange("excludedCodes", sanitizeDrgCodes(e.value ?? []))
              }
              placeholder="99213, 470"
              separator=","
            />
            <FieldHelp>
              Codes to skip from the rule. If a code here matches the claim at
              the header level, the rule does not apply at all. Service lines
              whose code matches one here are dropped from the pricing
              calculation and fall back to the fee schedule.
            </FieldHelp>
          </Field>
        </FieldGrid>

        <FieldGrid>
          <Field>
            <label htmlFor="packagedCodes">Packaged Codes</label>
            <Chips
              inputId="packagedCodes"
              value={formData.packagedCodes}
              onChange={(e) =>
                handleChange("packagedCodes", sanitizeDrgCodes(e.value ?? []))
              }
              placeholder="96372, 96374"
              separator=","
            />
            <FieldHelp>
              Codes the payer&apos;s policy never reimburses separately, even
              under a percent-of-charges contract (e.g. Cigna does not pay 96372
              at a facility ED visit). These lines are expected at{" "}
              <strong>$0</strong> — not priced at{" "}
              <code>multiplier × charges</code>, and not priced off the fee
              schedule either. Use <em>Excluded Codes</em> above instead when
              the code should still be paid, just from the fee schedule.
            </FieldHelp>
          </Field>
        </FieldGrid>

        {packagedAlsoExcluded.length > 0 && (
          <Message
            severity="warn"
            className="mb-4"
            text={`Code${
              packagedAlsoExcluded.length > 1 ? "s" : ""
            } ${packagedAlsoExcluded.join(", ")} appear${
              packagedAlsoExcluded.length === 1 ? "s" : ""
            } in both Excluded Codes and Packaged Codes — Packaged Codes will win, and ${
              packagedAlsoExcluded.length > 1 ? "these codes" : "this code"
            } will be expected at $0.`}
          />
        )}

        {overlappingCodes.length > 0 && (
          <Message
            severity="warn"
            className="mb-4"
            text={`Code${
              overlappingCodes.length > 1 ? "s" : ""
            } ${overlappingCodes.join(", ")} appear${
              overlappingCodes.length === 1 ? "s" : ""
            } in both lists — Excluded Codes will win.`}
          />
        )}

        <FieldGrid>
          <Field>
            <label htmlFor="groupName">Description</label>
            <InputText
              id="groupName"
              value={formData.groupName}
              onChange={(e) => handleChange("groupName", e.target.value)}
              placeholder="Optional contract reference (e.g. 'Lake Charles Employee')"
            />
          </Field>
        </FieldGrid>

        <Footer>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => navigate("/payer-rules")}
          />
          <Button
            label={submitLabel}
            icon="pi pi-check"
            onClick={onSubmit}
            disabled={!isValid}
            loading={isSubmitting}
          />
        </Footer>
      </Card>
    </div>
  );
}
